import React, {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import {selectHeaderActingPlayerBadgePlayer} from "../../../utils/gameState/user/selectHeaderActingPlayerBadgePlayer";
import {getPolicyImagePath} from "../../../utils/policy/getPolicyImagePath";
import {getPlayerNextPolicy} from "../../../utils/policy/getPlayerNextPolicy";
import {selectPhase} from "../../../redux/reducers/sharedStateReducer/selectors";
import {selectIsFieldCutscenePlaying} from "../../../redux/reducers/privateStateReducer/selectors";
import {PHASES} from "../../../constants/phases";
import {getConstructionActions} from "../../../utils/gameState/progress/getConstructionActions";
import {getPlayerActionsText} from "../../../utils/players/getPlayerActionsText";
import {CARD_OPEN_SOURCES, usePlayersListSideMenu} from "../../../contexts/PlayersListSideMenuContext";
import {REGISTRY_SINGLETON} from "../../../scenes/Registry";
import {GAME_PANEL_MATERIALS, GamePanel} from "../../gameUi/GamePanel";
import {StatWell} from "../../gameUi/StatWell";
import {LoadedImage} from "../../EmblaCarousel/FactionsCarousel";
import {joinClassNames} from "../../gameUi/joinClassNames";
import {useDesktopLayout} from "../../../hooks/visuals/useWideScreenLayout";
import './HeaderActingPlayerBadge.css';

/**
 * Положение плашки в координатах шапки: центр — под закладкой цвета игрока,
 * но плашка не выходит за края плиты строки игроков (полосы под шапкой, на
 * десктопе — самой шапки). tabX — где закладка над плашкой: туда смотрит
 * перемычка, у крайних игроков она уходит от центра плашки к ее краю
 */
const measureBadge = (badge, playerId) => {
  const header = badge.closest('.app-header');
  const playerNode = header?.querySelector(`.header-players-row [data-player-id="${playerId}"]`);
  const plate = playerNode?.closest('.gui-plate');
  if (!playerNode || !plate) {
    return null;
  }
  const headerLeft = header.getBoundingClientRect().left;
  const plateRect = plate.getBoundingClientRect();
  const playerRect = playerNode.getBoundingClientRect();
  // Ширина по содержимому дробная: округленная offsetWidth выпускала бы
  // плашку крайнего игрока за край плиты на доли пикселя
  const width = badge.getBoundingClientRect().width;
  const center = playerRect.left + playerRect.width / 2 - headerLeft;
  const minLeft = plateRect.left - headerLeft;
  const maxLeft = plateRect.right - headerLeft - width;
  const left = Math.min(Math.max(center - width / 2, minLeft), Math.max(maxLeft, minLeft));
  return {left, tabX: center - left};
};

/**
 * Плашка ходящего соперника (у зрителя — любого ходящего) под его кружком в
 * строке игроков шапки: эмаль цвета игрока, сверху политика и купленная на
 * следующий раунд, ниже монеты и очки с доходом, в строительстве еще
 * потраченные действия из всех (как в левой зоне шапки у своего игрока).
 * Кому и когда — selectHeaderActingPlayerBadgePlayer.
 *
 * Плашка висит с небольшим зазором под строкой, а с закладкой цвета ходящего
 * ее соединяет перемычка-трапеция той же эмали: так видно, чья плашка, даже
 * когда у крайних игроков она сдвинута внутрь строки. При смене ходящего
 * плашка переезжает к новому кружку (transition left и --badge-tab-x).
 *
 * Лежит прямо в шапке, а не в строке: плиты шапки и полосы игроков обрезаны
 * clip-path, плашка внутри них срезалась бы по их краю. Положение — замер
 * кружка ходящего (data-player-id) относительно шапки.
 *
 * Нажатие на политику открывает ее карточку, на ресурсы — список игроков,
 * как нажатие на строку; пока список открыт, плашка скрыта
 */
const HeaderActingPlayerBadge = ({isSpectatorMode = false}) => {
  const player = useSelector((state) => selectHeaderActingPlayerBadgePlayer(state, isSpectatorMode));
  // Облет захваченных городов (scenes/CityCaptureAnimation): ход нового раунда
  // в состоянии уже начался, но не ходит никто — плашка гаснет, как открытые
  // панели (gameUi/cityCapturePanelsFade), и возвращается после облета. Не по
  // снимку на старте, как панели: плашка появляется и посреди облета, когда
  // пустое снабжение закрылось сразу и началось строительство
  const isFieldCutscenePlaying = useSelector(selectIsFieldCutscenePlaying);
  // Строкой, а не объектом {used, total}: новый объект на каждый вызов
  // перерисовывал бы плашку на любое изменение состояния
  const actionsText = useSelector((state) => {
    if (!player || selectPhase(state) !== PHASES.CONSTRUCTION) {
      return null;
    }
    const {used, total} = getConstructionActions(state, player);
    return getPlayerActionsText(used, total);
  });
  const {isMenuOpen, openCard, toggleMenu} = usePlayersListSideMenu();
  // Строка игроков при смене раскладки монтируется заново (в шапке или в
  // полосе под ней) — следить нужно уже за новой
  const isDesktopLayout = useDesktopLayout();
  const badgeRef = useRef(null);
  const [placement, setPlacement] = useState(null);
  const playerId = player?.id;

  const updatePlacement = useCallback(() => {
    if (badgeRef.current && playerId) {
      setPlacement(measureBadge(badgeRef.current, playerId));
    }
  }, [playerId]);

  useLayoutEffect(() => {
    if (!playerId) {
      setPlacement(null);
      return undefined;
    }
    updatePlacement();

    // Строка игроков меняет ширину (загрузка портретов, смена раскладки) —
    // кружок ходящего сдвигается вместе с ней. Сама плашка — по ширине
    // содержимого: купленная политика или лишний разряд монет ее расширяют,
    // и у крайнего игрока она должна остаться в пределах плиты строки
    const header = badgeRef.current?.closest('.app-header');
    const row = header?.querySelector('.header-players-row');
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updatePlacement) : null;
    if (observer) {
      [header, row, badgeRef.current].filter(Boolean).forEach((node) => observer.observe(node));
    }
    window.addEventListener('resize', updatePlacement);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', updatePlacement);
    };
  }, [playerId, updatePlacement, isDesktopLayout]);

  // Переезд включается кадром позже первой расстановки: класс с transition,
  // добавленный вместе с первым left, повел бы плашку от левого края шапки
  const isPlaced = placement !== null;
  const [isMoving, setIsMoving] = useState(false);
  useEffect(() => {
    if (!isPlaced) {
      setIsMoving(false);
      return undefined;
    }
    const frame = requestAnimationFrame(() => setIsMoving(true));
    return () => cancelAnimationFrame(frame);
  }, [isPlaced]);

  if (!player) {
    return null;
  }

  const policy = player.policy;
  const nextPolicy = policy ? getPlayerNextPolicy(player) : null;

  const handlePolicyClick = (event, clickedPolicy) => {
    event.stopPropagation();
    REGISTRY_SINGLETON.getSoundManager().playCardOpen();
    // Источник — для шага обучения «карточка политики ходящего соперника»
    openCard(player, clickedPolicy, CARD_OPEN_SOURCES.ACTING_BADGE);
  };

  const handleStatsClick = (event) => {
    event.stopPropagation();
    REGISTRY_SINGLETON.getSoundManager().playSideMenuClick();
    toggleMenu();
  };

  return (
    <div
      ref={badgeRef}
      className={joinClassNames(
        'header-acting-badge',
        // До первого замера плашка невидима
        isPlaced && 'header-acting-badge_placed',
        isMoving && 'header-acting-badge_moving',
        // Список игроков выпадает поверх строки — плашка торчала бы над ним;
        // в списке те же сведения
        isMenuOpen && 'header-acting-badge_hidden',
        isFieldCutscenePlaying && 'header-acting-badge_city-capture-hidden',
      )}
      style={isPlaced ? {left: `${placement.left}px`, '--badge-tab-x': `${placement.tabX}px`} : undefined}
      data-player-id={player.id}
    >
      {/* Форма — своя: перемычка к закладке и плита со срезанными углами
          (--gui-shape в HeaderActingPlayerBadge.css) */}
      <GamePanel
        material={GAME_PANEL_MATERIALS.MINE}
        playerColor={player.color}
        className={'header-acting-badge_plate'}
      >
        <span className={'header-acting-badge_policies'}>
          {policy ? (
            <>
              <button
                type={'button'}
                className={'header-acting-badge_policy-button'}
                data-tutorial-select={'acting_badge_policy'}
                onClick={(event) => handlePolicyClick(event, policy)}
                title={policy.name}
              >
                <LoadedImage
                  className={'header-acting-badge_policy-img'}
                  src={getPolicyImagePath(policy.id)}
                  alt={policy.name}
                />
              </button>
              {nextPolicy && (
                <>
                  <img
                    className={'header-acting-badge_policy-arrow'}
                    src={'/img/icons/up white.png'}
                    alt={''}
                    aria-hidden={true}
                  />
                  <button
                    type={'button'}
                    className={'header-acting-badge_policy-button'}
                    onClick={(event) => handlePolicyClick(event, nextPolicy)}
                    title={`${nextPolicy.name} — со следующего раунда`}
                  >
                    <LoadedImage
                      className={'header-acting-badge_policy-img'}
                      src={getPolicyImagePath(nextPolicy.id)}
                      alt={nextPolicy.name}
                    />
                  </button>
                </>
              )}
            </>
          ) : (
            <span className={'header-acting-badge_no-policy'} title={'Политика не куплена'}>
              <img
                className={'header-acting-badge_no-policy-icon'}
                src={'/img/icons/governance.png'}
                alt={'Политика не куплена'}
              />
            </span>
          )}
        </span>

        <button
          type={'button'}
          className={'gui-stat-strip header-acting-badge_stats'}
          onClick={handleStatsClick}
          aria-label={`${player.name}: ${actionsText ? 'монеты, очки и действия' : 'монеты и очки'}. Список игроков`}
        >
          <StatWell
            bare
            icon={'/img/icons/coins.png'}
            value={player.coins}
            income={player.coinsIncome}
            title={'Монеты'}
          />
          <StatWell
            bare
            icon={'/img/icons/win points.png'}
            value={player.winPoints}
            income={player.winPointsIncome}
            title={'Очки победы'}
          />
          {actionsText && (
            <StatWell
              bare
              maskIcon
              icon={'/img/icons/action.png'}
              value={actionsText}
              title={'Действия строительства'}
            />
          )}
        </button>
      </GamePanel>
    </div>
  );
};

export default HeaderActingPlayerBadge;
